import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';

import { Box, Dialog, DialogContent, styled } from '@material-ui/core';

import { useDialogConfirm } from '@src/components/Common/Dialog';
import IconWrapper from '@src/components/Common/IconWrapper';
import IconButton from '@src/components/UI/IconButton';
import CallStartIcon from '@src/resources/icons/CallStartIcon';

import ContactCallPermissionsAlert from '@src/components/StartContactConversation/ContactCallPermissionsAlert';

import useCallAgentPermissionFlags from '@src/hooks/call/useCallAgentPermissionFlags';
import useHasCallAccess from '@src/hooks/call/useHasCallAccess';
import useCallPermissionFlags, {
  callPermissionFlags,
} from '@src/hooks/call/useCallPermissionFlags';
import useChatActions from '@src/hooks/useChatActions';
import useStartOutgoingCallMedia from '@src/logic/call/callMedia/hooks/useStartOutgoingCallMedia';
import { useLazyGetCallPermissionQuery } from '@src/reducers/callsAPI';

import usePreventMultipleClicks from '@src/hooks/usePreventMultipleClicks';
import { selectHasCallInActiveChat } from '@src/selectors/calls';
import { activeChatSelector } from '@src/selectors/chats';
import { getActiveChatContactInfo } from '@src/selectors/contact';
import getContactPhoneNumber from '@src/utils/contact';
import { removePhoneNumberPrefix } from '@src/utils/formatPhoneNumber';
import OutgoingCallTemplateForm from './OutgoingCallTemplateForm';

const StartOutgoingCallButton: React.FC = () => {
  const chatActions = useChatActions();
  const { formatMessage } = useIntl();
  const dialogConfirm = useDialogConfirm();
  const { isVisible, isEnabled } = useCallAgentPermissionFlags();

  const chat = useSelector(activeChatSelector);
  const activeContactInfo = useSelector(getActiveChatContactInfo);
  const hasCallInActiveChat = useSelector(selectHasCallInActiveChat);
  const { hasCallAccess } = useHasCallAccess(chat?.viaBotAccountId);

  const [isLoadingOutgoingCall, setIsLoadingOutgoingCall] = useState(false);
  const [openPermissionDialog, setOpenPermissionDialog] = useState(false);

  const startOutgoingCall = useStartOutgoingCallMedia();

  const [
    fetchPermissions,
    { data: permissions, isFetching: isFetchingPermissions },
  ] = useLazyGetCallPermissionQuery();

  const permissionFlags = useCallPermissionFlags(permissions);

  // The chat payload carries a reduced contact (often just cmaId, empty on some
  // backends). While the chat is open the contact-info panel already loads the
  // full record into Redux, so we merge its number in when it belongs to the
  // same contact — using data the chat already fetched rather than the racing
  // chat payload (ENG-1715).
  const effectiveContact = useMemo(() => {
    const base = chat?.contact;
    if (!base) return base;
    if (activeContactInfo?.id !== base.id) return base;
    return {
      ...base,
      mobileNumber: base.mobileNumber || activeContactInfo?.mobileNumber,
    };
  }, [chat?.contact, activeContactInfo]);

  // The contact's call identifier (phone or BSUID). Empty until the contact is
  // resolved from either source — the call action stays disabled while it is,
  // so we never query permissions without it (ENG-1715).
  const contactIdentifier = useMemo(
    () =>
      removePhoneNumberPrefix(
        effectiveContact?.cmaId || effectiveContact?.mobileNumber || ''
      ),
    [effectiveContact]
  );

  const showPermissionCheckError = () =>
    dialogConfirm({
      disableBackdropClose: true,
      title: formatMessage({ id: 'CallPermission.Error.TitleLbl' }),
      description: formatMessage({ id: 'CallPermission.Error.ContentLbl' }),
    });

  const onRequestOutgoingCall = async () => {
    setIsLoadingOutgoingCall(true);

    try {
      const contactId = effectiveContact
        ? getContactPhoneNumber(effectiveContact)
        : '';
      // The contact resolves from either the chat payload or the already-loaded
      // contact-info record. Without an identifier the permissions query yields
      // a 400 that must NOT be read as "no permission".
      if (!contactId) {
        showPermissionCheckError();
        return;
      }

      // cmaId can hold a BSUID (<CC>.<id>) or a phone number — route accordingly.
      // Meta BSUID format: ^[A-Z]{2}\.(ENT\.)?[A-Za-z0-9]{1,128}$
      const isBsuid = /^[A-Z]{2}\.(ENT\.)?[A-Za-z0-9]{1,128}$/.test(contactId);
      const { data: newPermissions, error } = await fetchPermissions(
        {
          phoneNumberId: chat.viaBotAccount,
          ...(isBsuid
            ? { contactBsuid: contactId }
            : { phoneNumber: contactId }),
        },
        false
      );

      // A failed permissions query is not the same as "no permission": surface
      // a clear error instead of prompting for a call-request template.
      if (error) {
        showPermissionCheckError();
        return;
      }

      const newPermissionFlags = callPermissionFlags(newPermissions);

      if (
        !newPermissionFlags.isStartCallAllowed ||
        newPermissionFlags.isStartCallExceeded
      ) {
        setOpenPermissionDialog(true);
        return;
      }

      const callePhone = removePhoneNumberPrefix(chat.viaBotAccountAlias);

      await startOutgoingCall({
        caller: {
          phoneNumber: callePhone,
          whatsappPhoneNumberId: chat.viaBotAccount,
        },
        callee: {
          phoneNumber: isBsuid
            ? removePhoneNumberPrefix(effectiveContact?.mobileNumber ?? '')
            : contactId,
          name: effectiveContact?.fullName ?? '',
          ...(isBsuid && { bsuid: contactId }),
        },
      });
    } finally {
      setIsLoadingOutgoingCall(false);
    }
  };

  const safeOnRequestOutgoingCall = usePreventMultipleClicks(
    onRequestOutgoingCall,
    { blockTimeMs: 3000 }
  );

  return (
    <>
      {/* ---------------------- PERMISSION / TEMPLATE DIALOG ---------------------- */}
      <Dialog
        open={openPermissionDialog}
        onClose={() => setOpenPermissionDialog(false)}
        style={{ zIndex: 1000 }}
        PaperProps={{
          style: { border: '2px solid #7C69FF' },
        }}
      >
        <DialogContent>
          <OutgoingCallTemplateForm
            onSubmit={selectedTemplate => {
              if (!selectedTemplate) return;

              setOpenPermissionDialog(false);
              chatActions.resumeChat(
                chat.id,
                String(selectedTemplate.templatedMsg.id)
              );
            }}
            disabled={
              permissionFlags.isSendCallTemplateExceeded ||
              permissionFlags.isStartCallExceeded
            }
          />

          {permissions &&
            (permissionFlags.isSendCallTemplateExceeded ||
              permissionFlags.isStartCallExceeded) && (
              <Box mt={1}>
                <ContactCallPermissionsAlert contactPermissions={permissions} />
              </Box>
            )}
        </DialogContent>
      </Dialog>

      {isVisible && hasCallAccess && (
        <CallIconButton
          disabled={
            isFetchingPermissions ||
            !isEnabled ||
            hasCallInActiveChat ||
            !contactIdentifier
          }
          loading={isLoadingOutgoingCall}
          onClick={safeOnRequestOutgoingCall}
        >
          <IconWrapper fontSize={22} color="#FFF">
            <CallStartIcon />
          </IconWrapper>
        </CallIconButton>
      )}
    </>
  );
};

const CallIconButton = styled(IconButton)(({ theme }) => ({
  width: 40,
  height: 40,
  cursor: 'pointer',

  '&:not(.Mui-disabled)': {
    backgroundColor: '#86cb7b',

    '&:hover': {
      backgroundColor: '#86cb7b',
    },
  },

  '&.Mui-disabled': {
    backgroundColor: theme.palette.action.disabledBackground,
  },
}));

export default StartOutgoingCallButton;
